import { Session } from 'next-auth';
import { permanentRedirect, redirect } from 'next/navigation';

import { getProductAnimco } from '@services/animco';
import { cmsService } from '@services/cms';
import { getWallConfig } from '@services/config';
import { logger } from '@services/loggers';
import { getDefaultPlan, getMobilePlans, getPlanTypeFromUrl } from '@services/products';
import { publicationsService } from '@services/publications';

import { PalpatineProduct } from '@app-types/api/palpatine';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { ProductNotFoundError } from '@app-types/error';
import { SearchParamsType } from '@app-types/page';
import { ProductParentFull } from '@app-types/product';

import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';
import { formatTagText } from '@helpers/tracking';

import { PlanTypeId } from '@constants/plan';
import { TAG_CATEGORIES } from '@constants/tracking';

import { getProductDetails } from '@product-detail/actions';
import { getCurrentProductGencode, shouldRedirectToFilteredWall } from '@product-detail/helpers';

import { ProductDetail } from './product-detail';

interface PhoneDetailsProps {
    isRenewal?: boolean;
    params: { slug: string };
    searchParams?: SearchParamsType;
    contract?: ContractType;
    session?: Session;
}

export async function PhoneDetails({
    isRenewal = false,
    params,
    searchParams,
    contract,
    session,
}: Readonly<PhoneDetailsProps>) {
    let product: ProductParentFull | undefined;
    let tradeInBonusPlan: string | undefined;
    let productStatics: PalpatineProduct[];
    const { slug } = params;
    try {
        [product, tradeInBonusPlan, { items: productStatics }] = await Promise.all([
            getProductDetails(slug, isRenewal),
            cmsService.getBonusPlan(),
            cmsService.getProduct(),
        ]);
    } catch (error) {
        if (error instanceof ProductNotFoundError) {
            logger.log('🚨 Phone not found', slug);
            const brandsWithFilter =
                getWallConfig(WallType.PHONE)
                    .filters.find((filter) => filter.key === 'marque')
                    ?.values?.map((brand) => brand.key) ?? [];
            const { shouldRedirect, brand } = shouldRedirectToFilteredWall(
                slug,
                brandsWithFilter,
                getWallConfig(WallType.PHONE),
            );

            if (shouldRedirect) {
                permanentRedirect(`${paths.phoneWall}/filtre/${brand}`);
            } else {
                permanentRedirect(paths.phoneWall);
            }
        }
        redirect(paths.phoneWall);
    }

    const productGencode = getCurrentProductGencode(product, slug);

    const currentProduct = product.childs[productGencode]!;

    const urlKey = typeof searchParams?.forfait === 'string' ? searchParams.forfait : '';
    const urlKeyType = getPlanTypeFromUrl(urlKey) || PlanTypeId.PREMIUM;

    const animco = getProductAnimco(productStatics, product.urlKey, isRenewal);

    const plansPromise = getMobilePlans(
        productGencode,
        isRenewal,
        urlKeyType,
        contract?.id,
        isRenewal ? contract?.subscription.offerId : undefined,
        isRenewal ? session : undefined,
        true,
        false,
    );

    const publicationId =
        productStatics.find((productStatic) => productStatic.Slug === product.urlKey)?.id_les_numeriques ?? '';
    const publicationsPromise = publicationsService.getProductsTestsPublications(publicationId ? [publicationId] : []);

    const [plans, publications] = await Promise.all([plansPromise, publicationsPromise]);

    const defaultPlanGencode = getDefaultPlan(false).toString();

    const currentPlan = isRenewal
        ? plans[0]
        : plans.find((plan) => plan.urlKey === urlKey || plan.gencode === defaultPlanGencode);

    return (
        <ProductDetail
            type={WallType.PHONE}
            baseUrl={isRenewal ? paths.renewalPhoneWall : paths.phoneWall}
            searchParams={searchParams}
            product={product}
            plans={plans}
            currentPlan={currentPlan}
            currentProduct={currentProduct}
            animco={animco}
            tradeInBonusPlan={tradeInBonusPlan}
            enableKonami
            contract={contract}
            session={session}
            isRenewal={isRenewal}
            slug={PAGE_SLUG.PHONE_PRODUCT}
            publication={publications[0]}
            trackingData={{
                page: formatTagText(`Telephone_${product.title}`),
                pageCategory: 'Fiche_produit_telephone',
                marketLine: 'Mobile',
                isB2B: false,
                eCommerceStep: 'Fiche_produit',
                variant: {
                    repriseMobile: `${product.tradeIn && product.tradeIn.bonus > 0 ? 'avec' : 'sans'}_bonus`,
                    repriseMobileBandeau: animco.box.length > 0 || animco.banner.length > 0,
                    forfaitDetenu: isRenewal ? contract?.subscription.label : undefined,
                },
                variables: {
                    stock: formatTagText(currentProduct.stockDetails.stock.label),
                },
                products: [
                    {
                        name: product.name,
                        id: currentProduct.entityId,
                        gencode: currentProduct.gencode,
                        sku: currentProduct.sku,
                        price: currentProduct.price ?? 0,
                        type_id: currentProduct.type,
                        manufacturer: product.manufacturer,
                        ligneProduit_niv2: TAG_CATEGORIES[currentProduct.type]!,
                        color: currentProduct.color,
                        capacity: currentProduct.capacity as string,
                    },
                ],
            }}
        />
    );
}
